import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Delete,
  ExpandMore,
  Visibility,
} from "@mui/icons-material";
import {
  emailIntervalHours,
  emailTimingLabel,
  stageCriterionUpdate,
} from "./editor";
import EmailContentEditorDialog from "./EmailContentEditorDialog";
import ConfirmationDialog from "./ConfirmationDialog";

export default function StageInspector({
  stage,
  stages,
  criteria,
  senders,
  transition,
  dispatch,
  onPreview,
  utmSuggestions,
  personalisationFields,
}) {
  const [contentEmailKey, setContentEmailKey] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  if (!stage)
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">
          Select a stage to configure it.
        </Typography>
      </Paper>
    );
  const criterion = criteria.find((c) => c.code === stage.criterionCode);
  const enabledDelays = stage.templates
    .filter((email) => email.isEnabled)
    .map((email) => Number(email.delayHours));
  const hasTimingConflict = enabledDelays.some(
    (delay, index) => index > 0 && delay - enabledDelays[index - 1] < 24,
  );
  const update = (value) =>
    dispatch({ type: "updateStage", key: stage.key, value });
  const changeCriterion = (code) => {
    update(stageCriterionUpdate(criteria, code, stage.isTerminal));
    if (code === "terminal")
      dispatch({ type: "disconnect", sourceKey: stage.key });
  };
  return (
    <Paper sx={{ p: 2 }}>
      {stage.isEntry && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          To send the first enabled email on enrollment, set its delay to zero
          and enable “Send the first email immediately on enrollment” in
          Delivery schedule. Only that email can bypass sending hours.
        </Typography>
      )}
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6">Stage details</Typography>
        <IconButton
          aria-label="Delete stage"
          color="error"
          onClick={() =>
            setConfirmation({
              title: "Delete stage?",
              message: "Delete this stage and all of its connections?",
              action: () => dispatch({ type: "deleteStage", key: stage.key }),
            })
          }
        >
          <Delete />
        </IconButton>
      </Stack>
      <Stack spacing={2} mt={2}>
        <TextField
          label="Stage name"
          value={stage.name}
          onChange={(e) => update({ name: e.target.value })}
          fullWidth
        />
        <TextField
          label="Description"
          value={stage.description || ""}
          onChange={(e) => update({ description: e.target.value })}
          multiline
          minRows={2}
          fullWidth
        />
        <Stack direction="row">
          <FormControlLabel
            control={
              <Checkbox
                checked={stage.isEntry}
                onChange={(e) => update({ isEntry: e.target.checked })}
              />
            }
            label="Entry stage"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={stage.isTerminal}
                disabled={stage.criterionCode === "terminal"}
                onChange={(e) => {
                  update({
                    isTerminal: e.target.checked,
                    ...(e.target.checked
                      ? { advanceAfterEmailSequence: false }
                      : {}),
                  });
                  if (e.target.checked)
                    dispatch({ type: "disconnect", sourceKey: stage.key });
                }}
              />
            }
            label="Terminal stage"
          />
        </Stack>
        <TextField
          select
          label={stage.isTerminal ? "Complete journey when" : "Advance when"}
          value={stage.criterionCode}
          onChange={(e) => changeCriterion(e.target.value)}
          helperText={
            stage.isTerminal
              ? "When this condition is met, the user exits the journey and any remaining emails in this stage are stopped."
              : "This is the desired action. When it is met, the user advances early and any remaining stage emails are stopped."
          }
        >
          {criteria.map((item) => (
            <MenuItem key={item.code} value={item.code}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
        {(criterion?.options || []).map((option) =>
          option.type === "boolean" ? (
            <FormControlLabel
              key={option.key}
              label={option.helpText}
              control={
                <Switch
                  checked={Boolean(stage.criterionConfig?.[option.key])}
                  disabled={option.readOnly}
                  onChange={(e) =>
                    update({
                      criterionConfig: {
                        ...stage.criterionConfig,
                        [option.key]: e.target.checked,
                      },
                    })
                  }
                />
              }
            />
          ) : (
            <TextField
              key={option.key}
              type="number"
              label={option.helpText}
              value={
                stage.criterionConfig?.[option.key] ?? option.default ?? ""
              }
              inputProps={{ min: option.minimum }}
              onChange={(e) =>
                update({
                  criterionConfig: {
                    ...stage.criterionConfig,
                    [option.key]: Number(e.target.value),
                  },
                })
              }
            />
          ),
        )}
        {!stage.isTerminal && (
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(stage.advanceAfterEmailSequence)}
                onChange={(e) =>
                  update({ advanceAfterEmailSequence: e.target.checked })
                }
              />
            }
            label="Advance after the final email"
          />
        )}
        {!stage.isTerminal && stage.advanceAfterEmailSequence && (
          <Typography variant="caption" color="text.secondary">
            If the desired action is not completed, the user will still advance
            after every enabled email in this stage has finished.
          </Typography>
        )}
        {!stage.isTerminal && (
          <TextField
            select
            label="Next stage"
            value={transition?.targetKey || ""}
            onChange={(e) =>
              e.target.value
                ? dispatch({
                    type: "connect",
                    sourceKey: stage.key,
                    targetKey: e.target.value,
                    outcome: criterion?.transitionOutcome || "criterion_met",
                  })
                : dispatch({ type: "disconnect", sourceKey: stage.key })
            }
          >
            <MenuItem value="">Not connected</MenuItem>
            {stages
              .filter((s) => s.key !== stage.key)
              .map((s) => (
                <MenuItem key={s.key} value={s.key}>
                  {s.name}
                </MenuItem>
              ))}
          </TextField>
        )}
      </Stack>
      <Divider sx={{ my: 3 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h6">Emails</Typography>
          <Typography variant="caption" color="text.secondary">
            Times are measured from entry into this stage.
          </Typography>
        </Box>
        <Button
          startIcon={<Add />}
          onClick={() => dispatch({ type: "addEmail", stageKey: stage.key })}
        >
          Add email
        </Button>
      </Stack>
      {stage.templates.filter((e) => e.isEnabled).length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This stage needs at least one enabled email before publication.
        </Alert>
      )}
      {hasTimingConflict && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Enabled emails must be scheduled at least 24 hours apart.
        </Alert>
      )}
      {stage.templates.map((email, index) => (
        <Accordion key={email.localKey} disableGutters>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" spacing={1} alignItems="center" width="100%">
              <Typography fontWeight={600} flex={1}>
                {email.name || `Email ${index + 1}`}
              </Typography>
              <Typography variant="caption">
                {emailTimingLabel(stage.templates, index)}
              </Typography>
              {!email.isEnabled && (
                <Typography variant="caption" color="text.secondary">
                  {email.subject && email.contentDocument?.content
                    ? "Disabled — not sent"
                    : "Incomplete draft — not sent"}
                </Typography>
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <IconButton
                  aria-label="Move email up"
                  disabled={!index}
                  onClick={() =>
                    dispatch({
                      type: "moveEmail",
                      stageKey: stage.key,
                      emailKey: email.localKey,
                      delta: -1,
                    })
                  }
                >
                  <ArrowUpward />
                </IconButton>
                <IconButton
                  aria-label="Move email down"
                  disabled={index === stage.templates.length - 1}
                  onClick={() =>
                    dispatch({
                      type: "moveEmail",
                      stageKey: stage.key,
                      emailKey: email.localKey,
                      delta: 1,
                    })
                  }
                >
                  <ArrowDownward />
                </IconButton>
                <FormControlLabel
                  control={
                    <Switch
                      checked={email.isEnabled}
                      onChange={(e) =>
                        dispatch({
                          type: "updateEmail",
                          stageKey: stage.key,
                          emailKey: email.localKey,
                          value: { isEnabled: e.target.checked },
                        })
                      }
                    />
                  }
                  label="Enabled"
                />
                <IconButton
                  color="error"
                  aria-label="Delete email"
                  onClick={() =>
                    setConfirmation({
                      title: "Delete email?",
                      message: "Delete this email from the stage?",
                      action: () =>
                        dispatch({
                          type: "deleteEmail",
                          stageKey: stage.key,
                          emailKey: email.localKey,
                        }),
                    })
                  }
                >
                  <Delete />
                </IconButton>
              </Stack>
              {email.sourceLabel && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(email.isClone)}
                        onChange={(event) =>
                          dispatch({
                            type: "updateEmail",
                            stageKey: stage.key,
                            emailKey: email.localKey,
                            value: {
                              isClone: event.target.checked,
                              ...(event.target.checked
                                ? { sendToMigratedStageParticipants: false }
                                : {}),
                            },
                          })
                        }
                      />
                    }
                    label="Treat as a clone of original email"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Based on {email.sourceLabel}. Clones keep their identity
                    when edited or reordered, so migrated participants will not
                    receive an email they have already received. Turn off for a
                    substantially different email.
                  </Typography>
                </>
              )}
              {!email.isClone && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(email.sendToMigratedStageParticipants)}
                        onChange={(event) =>
                          dispatch({
                            type: "updateEmail",
                            stageKey: stage.key,
                            emailKey: email.localKey,
                            value: {
                              sendToMigratedStageParticipants:
                                event.target.checked,
                            },
                          })
                        }
                      />
                    }
                    label="Send to migrated participants already in this stage"
                  />
                  <Typography variant="caption" color="text.secondary">
                    If this new email is before their saved position, send it
                    next and then resume their journey. Participants never
                    return to an earlier stage. Applies only when migrating to
                    this version.
                  </Typography>
                </>
              )}
              <TextField
                label="Email name"
                value={email.name || ""}
                onChange={(e) =>
                  dispatch({
                    type: "updateEmail",
                    stageKey: stage.key,
                    emailKey: email.localKey,
                    value: { name: e.target.value },
                  })
                }
              />
              <Button
                sx={{ alignSelf: "flex-start" }}
                onClick={() => setContentEmailKey(email.localKey)}
              >
                Edit email content
              </Button>
              <TextField
                type="number"
                label={
                  index === 0
                    ? "Hours after entering stage"
                    : "Hours after previous email"
                }
                value={emailIntervalHours(stage.templates, index)}
                inputProps={{ min: 0, step: 1 }}
                onChange={(e) =>
                  dispatch({
                    type: "updateEmailInterval",
                    stageKey: stage.key,
                    emailKey: email.localKey,
                    intervalHours: Number(e.target.value),
                  })
                }
              />
              <Button
                startIcon={<Visibility />}
                onClick={() => onPreview(email)}
              >
                Preview / test send
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
      <EmailContentEditorDialog
        open={Boolean(contentEmailKey)}
        email={stage.templates.find(
          (email) => email.localKey === contentEmailKey,
        )}
        sender={senders[0]}
        utmSuggestions={utmSuggestions}
        allowedFields={personalisationFields}
        onClose={() => setContentEmailKey(null)}
        onApply={(value) =>
          dispatch({
            type: "updateEmail",
            stageKey: stage.key,
            emailKey: contentEmailKey,
            value,
          })
        }
      />
      <ConfirmationDialog
        open={Boolean(confirmation)}
        title={confirmation?.title || "Confirm action"}
        message={confirmation?.message || "Continue?"}
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setConfirmation(null)}
        onConfirm={() => {
          confirmation?.action();
          setConfirmation(null);
        }}
      />
    </Paper>
  );
}
